import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Users, Trophy, Calendar, Clock, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';

interface Team {
  team_id: number;
  team_name: string;
  team_logo?: string;
}

interface VenueGame {
  id: string;
  game_title: string;
  game_date: string;
  game_time: string;
  field_location: string;
  game_code: string;
  home_team_id?: number;
  away_team_id?: number;
  home_team?: Team;
  away_team?: Team;
}

export const TeamGameRequestInterface = () => {
  const [gameCode, setGameCode] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<number | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [gameInfo, setGameInfo] = useState<VenueGame | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  useEffect(() => {
    if (user) {
      loadUserTeams();
    }
  }, [user]);

  useEffect(() => {
    if (gameCode.length >= 6) {
      lookupGame();
    } else {
      setGameInfo(null);
    }
  }, [gameCode]);

  const loadUserTeams = async () => {
    if (!user) return;

    try {
      // Get teams where user is owner or coach
      const { data: ownedTeams, error: ownedError } = await supabase
        .from('teams')
        .select('team_id, team_name, team_logo')
        .eq('created_by', user.id);

      const { data: coachedTeams, error: coachedError } = await supabase
        .from('team_coaches')
        .select(`
          teams!inner(team_id, team_name, team_logo)
        `)
        .eq('coach_id', user.id);

      if (ownedError) throw ownedError;
      if (coachedError) throw coachedError;

      const allTeams = [
        ...(ownedTeams || []),
        ...(coachedTeams?.map(tc => tc.teams).filter(Boolean) || [])
      ];

      // Remove duplicates
      const uniqueTeams = allTeams.filter((team, index, self) => 
        index === self.findIndex(t => t.team_id === team.team_id)
      );

      setTeams(uniqueTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load your teams');
    }
  };

  const lookupGame = async () => {
    if (!gameCode || gameCode.length < 6) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('venue_games')
        .select(`
          id,
          game_title,
          game_date,
          game_time,
          field_location,
          game_code,
          home_team_id,
          away_team_id,
          home_team:teams!venue_games_home_team_id_fkey(team_id, team_name, team_logo),
          away_team:teams!venue_games_away_team_id_fkey(team_id, team_name, team_logo)
        `)
        .eq('game_code', gameCode.toUpperCase())
        .eq('status', 'approved')
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          toast.error('Game not found. Please check the game code.');
        } else {
          throw error;
        }
        setGameInfo(null);
        return;
      }

      setGameInfo(data);
    } catch (error) {
      console.error('Error looking up game:', error);
      toast.error('Failed to lookup game');
      setGameInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const joinGame = async () => {
    if (!gameInfo || !selectedTeam || !user) {
      toast.error('Please select a team');
      return;
    }

    setSubmitting(true);
    try {
      // Determine which position is available
      const isHomeAvailable = !gameInfo.home_team_id;
      const isAwayAvailable = !gameInfo.away_team_id;

      if (!isHomeAvailable && !isAwayAvailable) {
        toast.error('This game is full');
        return;
      }

      // Check if team is already in this game
      if (gameInfo.home_team_id === selectedTeam || gameInfo.away_team_id === selectedTeam) {
        toast.error('Your team is already registered for this game');
        return;
      }

      // Assign team to available position
      const teamSide = isHomeAvailable ? 'home' : 'away';
      const updateField = teamSide === 'home' ? 'home_team_id' : 'away_team_id';
      const statusField = teamSide === 'home' ? 'home_team_status' : 'away_team_status';

      const { error: updateError } = await supabase
        .from('venue_games')
        .update({
          [updateField]: selectedTeam,
          [statusField]: 'accepted'
        })
        .eq('id', gameInfo.id);

      if (updateError) throw updateError;

      // Create game confirmation record
      const { error: confirmError } = await supabase
        .from('venue_game_confirmations')
        .insert({
          game_id: gameInfo.id,
          team_id: selectedTeam,
          team_side: teamSide,
          confirmed_by: user.id,
          confirmed_by_type: supabaseUser ? 'supabase' : 'internal',
          confirmation_status: true,
          confirmed_at: new Date().toISOString()
        });

      if (confirmError) throw confirmError;

      toast.success(`Successfully joined game as ${teamSide} team!`);
      
      // Refresh game info
      await lookupGame();
      
    } catch (error) {
      console.error('Error joining game:', error);
      toast.error('Failed to join game');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (time: string) => {
    try {
      const [hours, minutes] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return time;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!user) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">Please log in to join games</p>
        </CardContent>
      </Card>
    );
  }

  if (teams.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            You need to be a team owner or coach to join games.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Trophy className="w-6 h-6 text-primary" />
            Join Game with Code
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Game Code Input */}
          <div className="space-y-2">
            <Label htmlFor="game_code" className="text-foreground">Game Code</Label>
            <Input
              id="game_code"
              value={gameCode}
              onChange={(e) => setGameCode(e.target.value.toUpperCase())}
              placeholder="Enter game code (e.g., GX1234)"
              className="bg-background border-border text-foreground font-mono text-lg"
              maxLength={6}
            />
            <p className="text-sm text-muted-foreground">
              Get the game code from the venue, game organizer, or Find & Join Games page - no URL needed!
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Game Information */}
          {gameInfo && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4 bg-muted/10">
                <h3 className="font-semibold text-lg text-foreground mb-3">{gameInfo.game_title}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(gameInfo.game_date)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{formatTime(gameInfo.game_time)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{gameInfo.field_location || 'Location TBD'}</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-foreground">Home Team</h4>
                    {gameInfo.home_team_id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground">{gameInfo.home_team?.team_name}</span>
                        <Badge variant="default">Confirmed</Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Open</span>
                        <Badge variant="secondary">Available</Badge>
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg p-3">
                    <h4 className="font-medium mb-2 text-foreground">Away Team</h4>
                    {gameInfo.away_team_id ? (
                      <div className="flex items-center gap-2">
                        <span className="text-foreground">{gameInfo.away_team?.team_name}</span>
                        <Badge variant="default">Confirmed</Badge>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Open</span>
                        <Badge variant="secondary">Available</Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Team Selection and Join */}
              {(!gameInfo.home_team_id || !gameInfo.away_team_id) && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="team_select" className="text-foreground">Select Your Team</Label>
                    <Select
                      value={selectedTeam?.toString() || ''}
                      onValueChange={(value) => setSelectedTeam(parseInt(value))}
                    >
                      <SelectTrigger className="bg-background border-border text-foreground">
                        <SelectValue placeholder="Choose a team to register" />
                      </SelectTrigger>
                      <SelectContent>
                        {teams.map((team) => (
                          <SelectItem 
                            key={team.team_id} 
                            value={team.team_id.toString()}
                            disabled={team.team_id === gameInfo.home_team_id || team.team_id === gameInfo.away_team_id}
                          >
                            <div className="flex items-center gap-2">
                              {team.team_logo && (
                                <img src={team.team_logo} alt="" className="w-5 h-5 rounded" />
                              )}
                              <span>{team.team_name}</span>
                              {(team.team_id === gameInfo.home_team_id || team.team_id === gameInfo.away_team_id) && (
                                <Badge variant="outline" className="ml-2 text-xs">Already in game</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={joinGame}
                    disabled={!selectedTeam || submitting}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {submitting ? 'Joining...' : 'Join Game'}
                  </Button>
                </div>
              )}

              {/* Game Full Message */}
              {gameInfo.home_team_id && gameInfo.away_team_id && (
                <div className="text-center py-4">
                  <Badge variant="outline" className="text-lg px-4 py-2">
                    Game Full - Both teams confirmed
                  </Badge>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};